import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import {
  Banknote,
  Check,
  Eye,
  Pencil,
  RotateCcw,
  X
} from "lucide-react";
import { toast } from "sonner";

import { StatusBadge } from "@/components/StatusBadge";
import { AdvanceAmountsCell } from "@/components/requests/AdvanceAmountsCell";
import { Button } from "@/components/ui/button";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { SplitActionButton } from "@/components/SplitActionButton";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdvanceDetailDialog } from "@/components/requests/AdvanceDetailDialog";
import { FinanceNoticeDialog } from "@/components/requests/FinanceNoticeDialog";
import { DownloadReportButton } from "@/components/requests/DownloadReportButton";
import { RequestDetailDialog } from "@/components/requests/RequestDetailDialog";
import { SettleRequestDialog } from "@/components/requests/SettleRequestDialog";
import {
  AmountCellById,
  SettlementAmountCellById
} from "@/components/requests/SettlementAmountCell";
import {
  ReviewAdvanceDialog,
  ReviewAdvanceFields
} from "@/components/requests/ReviewAdvanceDialog";
import {
  ReviewRequestDialog,
  ReviewRequestFields
} from "@/components/requests/ReviewRequestDialog";
import { TripCell } from "@/components/requests/TripCell";
import { tripRoute } from "@/lib/trip";
import { DataTable } from "@/components/data-table/DataTable";
import { SortableHeader } from "@/components/data-table/SortableHeader";
import { type AppTableFeatures } from "@/components/data-table/features";
import SegmentedTabs from "@/components/SegmentedTabs";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { RefreshButton } from "@/components/RefreshButton";
import {
  ApiError,
  approveAdvanceAndNotifyFinance,
  approveAndPayAdvance,
  decideAdvanceApproval,
  decideFinanceApproval,
  fetchAdvancesAwaitingPayment,
  fetchFinanceQueue,
  markAdvancePaid,
  rejectAdvanceAsFinance,
  resubmitAdvanceApproval,
  resubmitAdvanceFinance,
  resubmitFinanceApproval,
  reviewAdvanceApproval,
  reviewAdvanceFinance,
  reviewFinanceApproval,
  settleRequest
} from "../api";
import {
  Advance,
  AdvanceAmount,
  AdvanceStatus,
  ApprovalStatus,
  FinanceQueueRow,
  requestTypeLabel
} from "../types";
import { formatDate } from "@/lib/datetime";

type View = "requests" | "advances";
type DisplayStatus = ApprovalStatus | "Completed" | "ResubmitRequested";
type StatusFilter = "All" | DisplayStatus;

const STATUS_LABEL: Record<Exclude<StatusFilter, "All">, string> = {
  Pending: "Pending",
  Approved: "Approved",
  Rejected: "Rejected",
  Completed: "Completed",
  ResubmitRequested: "Needs changes"
};

const EMPTY_COPY: Record<StatusFilter, string> = {
  All: "Nothing is currently routed to you.",
  Pending: "No pending sign-offs.",
  Approved: "No approved decisions yet.",
  Rejected: "No rejected decisions yet.",
  Completed: "No settled requests yet.",
  ResubmitRequested: "No requests are awaiting changes from the employee."
};

/** The Status column's badge reads row.status, which is financeApproval's
 *  OWN decision status (Pending/Approved/Rejected) - permanently "Approved"
 *  once Finance ever approves, even after the travel request later reaches
 *  Completed (settled) via settle_request. row.requestStatus carries the
 *  real top-level status, so when it diverges from the finance-approval
 *  status we must show it instead:
 *    - Completed: Finance stays "Approved" forever after settlement - show
 *      Completed so a settled request isn't confused with one still
 *      Approved-but-unsettled.
 *    - ResubmitRequested: Finance SENT THE REQUEST BACK for changes
 *      (resubmit_finance_approval deliberately leaves financeApproval.status
 *      at "Pending" so the employee's eventual resubmission routes straight
 *      back to the same approver). Without this the row keeps reading
 *      "Pending" and still offers Approve/Reject even though the ball is in
 *      the employee's court - see the actions column below.
 *  See requests_api.py's settle_request / resubmit_finance_approval and
 *  db/approvals_store.py's _to_dict_from_finance for where these fields
 *  come from. */
function displayStatus(row: FinanceQueueRow): DisplayStatus {
  if (row.requestStatus === "Completed") return "Completed";
  if (row.requestStatus === "ResubmitRequested") return "ResubmitRequested";
  return row.status;
}

// "Needs action" isn't a real AdvanceStatus (it's Pending routed to this
// approver + org-wide Approved-awaiting-payment, combined - see
// requests_api.py's advances_awaiting_payment) - it's this table's own
// default view, so it gets its own filter value distinct from the four
// real AdvanceStatus values. Paid/Rejected/All are fetched with ?status=
// (see fetchAdvancesAwaitingPayment); "Needs action" is the plain no-param
// call.
type AdvanceStatusFilter =
  | "NeedsAction"
  | "All"
  | "Paid"
  | "Rejected"
  | "Resubmit";

const ADVANCE_EMPTY_COPY: Record<AdvanceStatusFilter, string> = {
  NeedsAction: "Nothing needs your attention right now.",
  All: "No advances have been routed to you yet.",
  Paid: "No advances have been marked paid yet.",
  Rejected: "No rejected advances yet.",
  Resubmit: "No advances are awaiting changes from the employee."
};

const ADVANCE_STATUS_LABEL: Record<AdvanceStatus, string> = {
  Pending: "Needs decision",
  Approved: "Awaiting payment",
  Rejected: "Rejected",
  Paid: "Paid",
  Resubmit: "Needs changes"
};

const columnHelper = createColumnHelper<AppTableFeatures, FinanceQueueRow>();
const advanceColumnHelper = createColumnHelper<AppTableFeatures, Advance>();

/** Small red count pill next to a tab label - "there's something here
 *  needing your action," same idea as an unread-message badge. Caller only
 *  renders it when count > 0 (see the two call sites below), so it never
 *  shows a bare "0". Capped display at "9+" so a large count doesn't blow
 *  out the tab's width. */
function TabCountBadge({ count }: { count: number }) {
  return (
    <span className="flex h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full bg-destructive px-1 text-[0.625rem] font-semibold leading-none text-destructive-foreground">
      {count > 9 ? "9+" : count}
    </span>
  );
}

export default function ApproverQueue() {
  // Hoisted above `view`'s own declaration, which needs it: a notification
  // deep-link (?request=<id> / ?advance=<id>, see notifications.py's
  // _dispatch) must land on the MATCHING tab, not always "requests" - the
  // same bug MyApprovals.tsx had (?advance=<id> opened the right sheet, but
  // left the tab underneath on "requests" regardless, so closing it showed
  // the wrong table).
  const [searchParams] = useSearchParams();
  // ?view=advances (the Dashboard's "Approver Queue" advances box, same
  // pattern as MyRequests.tsx/OtherReimbursements.tsx's own ?view= param)
  // also opens on the Advances tab, same as a notification's ?advance=<id>.
  const [view, setView] = React.useState<View>(() =>
    searchParams.get("view") === "advances" || searchParams.get("advance")
      ? "advances"
      : "requests"
  );
  const [rows, setRows] = React.useState<FinanceQueueRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [status, setStatus] = React.useState<StatusFilter>("Pending");
  const [decision, setDecision] = React.useState<{
    row: FinanceQueueRow;
    verdict: ApprovalStatus;
  } | null>(null);
  const [comment, setComment] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [settleTarget, setSettleTarget] =
    React.useState<FinanceQueueRow | null>(null);
  const [settling, setSettling] = React.useState(false);

  const [advances, setAdvances] = React.useState<Advance[]>([]);
  const [advancesLoading, setAdvancesLoading] = React.useState(true);
  const [advanceStatus, setAdvanceStatus] =
    React.useState<AdvanceStatusFilter>("NeedsAction");
  // Independent of `advances`/`advanceStatus` above - the tab badge always
  // means "how many advances currently need MY action" (Pending routed to
  // me + Approved-awaiting-payment, same as fetchAdvancesAwaitingPayment()
  // with no status), regardless of which status filter the Advances table
  // itself is currently showing (e.g. viewing the "Paid" history tab
  // shouldn't make this badge read 0 just because Paid rows have nothing
  // to action).
  const [needsActionAdvanceCount, setNeedsActionAdvanceCount] =
    React.useState(0);
  const [payingId, setPayingId] = React.useState<string | null>(null);
  // Confirmed before either payAdvance or approveAndPay below actually
  // fires - disbursing cash has no undo in this UI, same reasoning as
  // settleTarget's own confirm step for a travel request.
  const [payTarget, setPayTarget] = React.useState<Advance | null>(null);
  const [advanceDecision, setAdvanceDecision] = React.useState<{
    advance: Advance;
  } | null>(null);
  const [advanceComment, setAdvanceComment] = React.useState("");
  const [advanceSaving, setAdvanceSaving] = React.useState(false);

  // Deep-link support: a notification's "View Request" button appends
  // ?request=<id> or ?advance=<id> (see notifications.py's _dispatch) -
  // same reasoning/pattern as MyApprovals.tsx's identical handling
  // (searchParams itself is hoisted above - see `view`'s own declaration).
  const [viewRequestId, setViewRequestId] = React.useState<string | null>(
    () => searchParams.get("request")
  );
  const [viewAdvanceId, setViewAdvanceId] = React.useState<string | null>(
    () => searchParams.get("advance")
  );
  const [highlightOnOpen, setHighlightOnOpen] = React.useState(
    () => !!searchParams.get("request") || !!searchParams.get("advance")
  );
  // Used for the sheet's own GENUINE close (X, outside click, Escape) -
  // clears highlightOnOpen too, same reasoning/pattern as MyApprovals.tsx's
  // identical helpers.
  const closeViewRequestSheet = () => {
    setViewRequestId(null);
    setHighlightOnOpen(false);
  };
  const closeViewAdvanceSheet = () => {
    setViewAdvanceId(null);
    setHighlightOnOpen(false);
  };
  // "Stepping aside" - a footer action (Approve/Review/Reject/Resubmit/
  // Settle/Mark paid) needs the sheet closed so its own dialog doesn't
  // double up two semi-transparent overlays into solid black - but closing
  // outright was itself wrong: if the approver then CANCELS that dialog
  // without deciding anything, they'd land on the bare table instead of
  // back on the sheet they were reading. These remember "which sheet to
  // bring back, and with which id" so the opened dialog's own
  // onOpenChange(false) - which ONLY ever fires for a genuine user-driven
  // cancel/X/outside-click/Escape, never for that same dialog's own
  // programmatic close-on-save - can restore it. Same pattern as
  // MyApprovals.tsx's identical returnTo*Sheet state.
  const [returnToRequestSheet, setReturnToRequestSheet] = React.useState<
    string | null
  >(null);
  const [returnToAdvanceSheet, setReturnToAdvanceSheet] = React.useState<
    string | null
  >(null);
  const stepAsideFromRequestSheet = () => {
    if (viewRequestId) setReturnToRequestSheet(viewRequestId);
    setViewRequestId(null);
  };
  const stepAsideFromAdvanceSheet = () => {
    if (viewAdvanceId) setReturnToAdvanceSheet(viewAdvanceId);
    setViewAdvanceId(null);
  };
  const cancelAndMaybeReturnToRequestSheet = (closeDialog: () => void) => {
    closeDialog();
    if (returnToRequestSheet) {
      setViewRequestId(returnToRequestSheet);
      setReturnToRequestSheet(null);
    }
  };
  const cancelAndMaybeReturnToAdvanceSheet = (closeDialog: () => void) => {
    closeDialog();
    if (returnToAdvanceSheet) {
      setViewAdvanceId(returnToAdvanceSheet);
      setReturnToAdvanceSheet(null);
    }
  };

  // Review: editable version of the detail dialog, own component (not a
  // mode of the read-only RequestDetailDialog/AdvanceDetailDialog). Finance
  // has no preview-popup Approve today (unlike the Manager stage in
  // MyApprovals.tsx), so saving a request's review decides it outright; an
  // advance's review-save path depends on WHICH stage this row actually is
  // (reviewAdvanceStage below) - Pending/no-manager routes through the
  // Manager-stage review+approve (still needs a Finance-notify popup, so it
  // mirrors MyApprovals.tsx's deferred-commit shape instead), Approved
  // routes through the Finance-stage review (just a field save, nothing
  // further to approve - Mark paid/Reject stays a separate action).
  const [reviewRequestId, setReviewRequestId] = React.useState<string | null>(
    null
  );
  const [reviewAdvance, setReviewAdvance] = React.useState<{
    id: string;
    stage: "Manager" | "Finance";
  } | null>(null);
  const [reviewSaving, setReviewSaving] = React.useState(false);
  // Only the Manager-stage (Pending/no-manager) advance review needs this -
  // same deferred Finance-notify-popup shape MyApprovals.tsx's Review uses.
  const [financeNotice, setFinanceNotice] = React.useState<{
    id: string;
    toName: string | null;
    toEmail: string | null;
  } | null>(null);
  const [financeCc, setFinanceCc] = React.useState<string[]>([]);
  const [financeSending, setFinanceSending] = React.useState(false);
  const [financeError, setFinanceError] = React.useState<string | null>(null);

  // Resubmit: single-click commit, comment required.
  const [resubmitTarget, setResubmitTarget] = React.useState<{
    kind: "request" | "advance";
    id: string;
    stage: "Manager" | "Finance";
  } | null>(null);
  const [resubmitComment, setResubmitComment] = React.useState("");
  const [resubmitSaving, setResubmitSaving] = React.useState(false);
  const [resubmitError, setResubmitError] = React.useState<string | null>(
    null
  );

  const handleStaleDecision = (err: unknown, reload: () => void) => {
    if (err instanceof ApiError && err.status === 409) {
      toast.error(
        err.message ||
          "This item was already acted on elsewhere (maybe another tab or approver) - the list has been refreshed to show its current status."
      );
      reload();
      return true;
    }
    return false;
  };

  const load = React.useCallback(() => {
    setLoading(true);
    fetchFinanceQueue()
      .then(setRows)
      .finally(() => setLoading(false));
  }, []);
  React.useEffect(() => {
    load();
  }, [load]);

  // Separate from `load` above - this table's own status filter drives a
  // DIFFERENT query server-side (see fetchAdvancesAwaitingPayment/
  // requests_api.py's advances_awaiting_payment), not a client-side filter
  // over one fetched list, so it needs its own fetch keyed on advanceStatus.
  const loadAdvances = React.useCallback(() => {
    setAdvancesLoading(true);
    fetchAdvancesAwaitingPayment(
      advanceStatus === "NeedsAction" ? undefined : advanceStatus
    )
      .then((result) => {
        setAdvances(result);
        // Already the "needs action" set in this case - reuse it instead
        // of firing a second, redundant request for the same data.
        if (advanceStatus === "NeedsAction")
          setNeedsActionAdvanceCount(result.length);
      })
      .finally(() => setAdvancesLoading(false));
  }, [advanceStatus]);
  React.useEffect(() => {
    loadAdvances();
  }, [loadAdvances]);

  // Keeps the Advances tab's badge accurate even while a DIFFERENT status
  // filter (Paid/Rejected) is the one actually loaded into `advances` above
  // - runs once on mount, and again after any action that could change the
  // count (see payAdvance/submitAdvanceDecision below). Skipped whenever
  // loadAdvances already just fetched this exact set (see above).
  const loadNeedsActionAdvanceCount = React.useCallback(() => {
    if (advanceStatus === "NeedsAction") return;
    fetchAdvancesAwaitingPayment().then((result) =>
      setNeedsActionAdvanceCount(result.length)
    );
  }, [advanceStatus]);
  React.useEffect(() => {
    loadNeedsActionAdvanceCount();
  }, [loadNeedsActionAdvanceCount]);

  const payAdvance = React.useCallback(
    async (advanceId: string) => {
      setPayingId(advanceId);
      try {
        await markAdvancePaid(advanceId);
        loadAdvances();
        loadNeedsActionAdvanceCount();
      } catch (err) {
        // Two Finance tabs both clicking Mark paid on the same advance - the
        // second one 409s; same toast+reload handling as every other
        // decision on this page, so it's clear here too rather than
        // silently doing nothing.
        if (!handleStaleDecision(err, loadAdvances)) {
          toast.error(
            err instanceof ApiError
              ? err.message
              : "Could not mark this advance as paid - check your connection and try again."
          );
        }
      } finally {
        setPayingId(null);
      }
    },
    [loadAdvances, loadNeedsActionAdvanceCount]
  );

  // Same "Mark paid" action, for a Pending advance that skipped straight to
  // Finance (no reporting manager on file) - there's no separate "Approved,
  // awaiting payment" stage to pass through first in that case, so this one
  // click does both (see requests_api.py's approve_and_pay_advance). Shares
  // payingId with payAdvance above since a row is only ever one or the
  // other, never both at once.
  const approveAndPay = React.useCallback(
    async (advanceId: string) => {
      setPayingId(advanceId);
      try {
        await approveAndPayAdvance(advanceId);
        loadAdvances();
        loadNeedsActionAdvanceCount();
      } catch (err) {
        // Same concurrent-action handling as payAdvance above.
        if (!handleStaleDecision(err, loadAdvances)) {
          toast.error(
            err instanceof ApiError
              ? err.message
              : "Could not approve and mark this advance as paid - check your connection and try again."
          );
        }
      } finally {
        setPayingId(null);
      }
    },
    [loadAdvances, loadNeedsActionAdvanceCount]
  );

  // The confirm step's own action - dispatches to whichever of the two
  // above actually applies, based on the target's current status (a
  // Pending row still needs the Manager-stage decision done too; an
  // Approved one is already past that and only needs paying).
  const confirmMarkPaid = async () => {
    if (!payTarget) return;
    const advanceId = payTarget.id;
    setPayTarget(null);
    // This flow concludes here regardless of outcome (payAdvance/
    // approveAndPay only toast on failure, no further chained dialog) -
    // clear any pending return-to-sheet now, same reasoning as every other
    // terminal point on this page.
    setReturnToAdvanceSheet(null);
    if (payTarget.status === "Pending") {
      await approveAndPay(advanceId);
    } else {
      await payAdvance(advanceId);
    }
  };

  const data = React.useMemo(
    () =>
      status === "All" ? rows : rows.filter((r) => displayStatus(r) === status),
    [rows, status]
  );
  // Same "needs my action" idea as needsActionAdvanceCount above - derived
  // straight from `rows` (unfiltered by the tab's own status selector), so
  // it stays accurate regardless of which status tab is currently open.
  const pendingRequestCount = React.useMemo(
    () => rows.filter((r) => displayStatus(r) === "Pending").length,
    [rows]
  );

  const submitDecision = async () => {
    if (!decision) return;
    if (decision.verdict === "Rejected" && !comment.trim()) return;
    setSaving(true);
    try {
      await decideFinanceApproval(
        decision.row.travelRequestId,
        decision.verdict as "Approved" | "Rejected",
        decision.verdict === "Rejected" ? comment.trim() : comment || undefined
      );
      setDecision(null);
      setComment("");
      // Unlike MyApprovals.tsx's Manager-stage Approve, Finance's decision
      // here is outright - no further preview/notify-Finance chain - so
      // this flow has genuinely concluded either way. Clear any pending
      // return-to-sheet now, or it would wrongly survive to reopen a stale
      // sheet on some LATER, unrelated dialog's cancel.
      setReturnToRequestSheet(null);
      load();
    } catch (err) {
      // This dialog has no inline error area, so every failure is toasted -
      // a stale decision (e.g. another tab already decided this row) also
      // closes the dialog and reloads via handleStaleDecision; anything
      // else leaves it open so Finance can retry without reselecting.
      if (!handleStaleDecision(err, load)) {
        toast.error(
          err instanceof ApiError
            ? err.message
            : `Could not ${decision.verdict === "Approved" ? "approve" : "reject"} this request - check your connection and try again.`
        );
      } else {
        setReturnToRequestSheet(null);
      }
    } finally {
      setSaving(false);
    }
  };

  // Reject-only - this dialog's Approve path for a Pending (no-manager)
  // advance is handled directly by approveAndPay above instead, same as the
  // Approved row's own "Mark paid" button; this confirm step only ever
  // fires for a rejection now.
  const submitAdvanceDecision = async () => {
    if (!advanceDecision) return;
    if (!advanceComment.trim()) return;
    setAdvanceSaving(true);
    try {
      if (advanceDecision.advance.status === "Approved") {
        // The Manager already approved this one and it's only waiting to be
        // paid - Finance's own rejection, a different route from rejecting a
        // Pending advance (see requests_api.py's finance_reject_advance).
        await rejectAdvanceAsFinance(
          advanceDecision.advance.id,
          advanceComment.trim()
        );
      } else {
        // Pending, routed straight to Finance (no reporting manager on
        // file) - a plain, immediate rejection (requests_api.py's
        // decide_advance_approval).
        await decideAdvanceApproval(
          advanceDecision.advance.id,
          "Rejected",
          advanceComment.trim()
        );
      }
      setAdvanceDecision(null);
      setAdvanceComment("");
      // This flow has genuinely concluded (reject is final, no further
      // chained dialog) - clear any pending return-to-sheet now, same
      // reasoning as submitDecision's identical clear above.
      setReturnToAdvanceSheet(null);
      loadAdvances();
      loadNeedsActionAdvanceCount();
    } catch (err) {
      // Same reasoning as submitDecision's identical catch above.
      if (!handleStaleDecision(err, loadAdvances)) {
        toast.error(
          err instanceof ApiError
            ? err.message
            : "Could not reject this advance - check your connection and try again."
        );
      } else {
        setReturnToAdvanceSheet(null);
      }
    } finally {
      setAdvanceSaving(false);
    }
  };

  const submitSettle = async (settledAmounts: AdvanceAmount[]) => {
    if (!settleTarget) return;
    setSettling(true);
    try {
      await settleRequest(settleTarget.travelRequestId, settledAmounts);
      setSettleTarget(null);
      // Settling concludes this flow - clear any pending return-to-sheet,
      // same reasoning as every other terminal point on this page.
      setReturnToRequestSheet(null);
      load();
    } catch (err) {
      // Two tabs both settling the same request - the second one 409s
      // (settle_request's own status guard, same shape as StaleStatusError).
      // No inline error area here, so toast either way; a stale decision
      // also closes the dialog and reloads.
      if (!handleStaleDecision(err, load)) {
        toast.error(
          err instanceof ApiError
            ? err.message
            : "Could not record the settlement - check your connection and try again."
        );
      } else {
        setReturnToRequestSheet(null);
      }
    } finally {
      setSettling(false);
    }
  };

  // Finance's Review on a travel request - unlike the Manager stage, this
  // decides outright (no Notify-Finance-style popup; Finance's plain
  // Approve has none either).
  const saveRequestReview = async (fields: ReviewRequestFields) => {
    if (!reviewRequestId) return;
    setReviewSaving(true);
    try {
      await reviewFinanceApproval(reviewRequestId, fields);
      setReviewRequestId(null);
      // Decides outright (no further chained dialog) - this flow has
      // genuinely concluded, same reasoning as every other terminal point
      // on this page.
      setReturnToRequestSheet(null);
      load();
    } catch (err) {
      if (!handleStaleDecision(err, load)) {
        toast.error(
          err instanceof ApiError
            ? err.message
            : "Could not save your edits to this request - check your connection and try again."
        );
      } else {
        setReturnToRequestSheet(null);
      }
    } finally {
      setReviewSaving(false);
    }
  };

  // Advance's Review - which endpoint depends on which stage this row
  // actually is. Manager-stage (Pending/no-manager) ends in a preview, same
  // deferred-commit shape as MyApprovals.tsx's own advance Review; Finance-
  // stage (Approved/awaiting-payment) just saves the fields, nothing to
  // approve - Mark paid/Reject are separate, unaffected actions.
  const saveAdvanceReview = async (fields: ReviewAdvanceFields) => {
    if (!reviewAdvance) return;
    setReviewSaving(true);
    try {
      if (reviewAdvance.stage === "Manager") {
        const preview = await reviewAdvanceApproval(reviewAdvance.id, fields);
        setFinanceCc([]);
        setFinanceError(null);
        setFinanceNotice({
          id: reviewAdvance.id,
          toName: preview.financeApproverName,
          toEmail: preview.financeApproverEmail
        });
        setReviewAdvance(null);
      } else {
        await reviewAdvanceFinance(reviewAdvance.id, fields);
        setReviewAdvance(null);
        // Finance-stage review just saves fields, nothing further to
        // approve (Mark paid/Reject are separate) - this flow has
        // genuinely concluded, unlike the Manager-stage branch above which
        // defers to the Finance-notice popup (see sendFinanceNotice for
        // where THAT flow's own return-to-sheet clear lives).
        setReturnToAdvanceSheet(null);
        loadAdvances();
      }
    } catch (err) {
      if (!handleStaleDecision(err, loadAdvances)) {
        toast.error(
          err instanceof ApiError
            ? err.message
            : "Could not save your edits to this advance - check your connection and try again."
        );
      } else {
        setReturnToAdvanceSheet(null);
      }
    } finally {
      setReviewSaving(false);
    }
  };

  // The actual commit for a Manager-stage advance Review - fired once
  // Finance clicks Send on the popup saveAdvanceReview's Manager branch
  // opened. Mirrors MyApprovals.tsx's sendFinanceNotice, scoped to just the
  // advance case since this page has no travel-request equivalent (Finance
  // deciding a travel request has no preview step to defer).
  const sendFinanceNotice = async () => {
    if (!financeNotice) return;
    setFinanceSending(true);
    setFinanceError(null);
    try {
      const cc = financeCc.length > 0 ? financeCc : undefined;
      await approveAdvanceAndNotifyFinance(financeNotice.id, undefined, cc);
      setFinanceNotice(null);
      // The Manager-stage advance Review -> Finance-notice chain genuinely
      // concludes here - clear any pending return-to-sheet now, same
      // reasoning as every other terminal point on this page.
      setReturnToAdvanceSheet(null);
      loadAdvances();
      loadNeedsActionAdvanceCount();
    } catch (err) {
      // A stale decision here (the advance was decided elsewhere between
      // opening this popup and clicking Send) closes the popup with a toast
      // and reloads - same handling as MyApprovals.tsx's identical popup.
      if (!handleStaleDecision(err, loadAdvances)) {
        setFinanceError(
          err instanceof ApiError
            ? err.message
            : "Could not send this for approval - check your connection and try again."
        );
      } else {
        setFinanceNotice(null);
        setReturnToAdvanceSheet(null);
      }
    } finally {
      setFinanceSending(false);
    }
  };

  const submitResubmit = async () => {
    if (!resubmitTarget || !resubmitComment.trim()) return;
    setResubmitSaving(true);
    setResubmitError(null);
    try {
      // Resubmit has no further chained dialog - concludes right here, so
      // clear whichever return-to-sheet this id might have pending, same
      // reasoning as every other terminal point on this page.
      if (resubmitTarget.kind === "request") {
        await resubmitFinanceApproval(
          resubmitTarget.id,
          resubmitComment.trim()
        );
        setReturnToRequestSheet(null);
        load();
      } else if (resubmitTarget.stage === "Manager") {
        await resubmitAdvanceApproval(
          resubmitTarget.id,
          resubmitComment.trim()
        );
        setReturnToAdvanceSheet(null);
        loadAdvances();
        loadNeedsActionAdvanceCount();
      } else {
        await resubmitAdvanceFinance(
          resubmitTarget.id,
          resubmitComment.trim()
        );
        setReturnToAdvanceSheet(null);
        loadAdvances();
        loadNeedsActionAdvanceCount();
      }
      setResubmitTarget(null);
      setResubmitComment("");
    } catch (err) {
      const reload =
        resubmitTarget.kind === "request" ? load : loadAdvances;
      if (!handleStaleDecision(err, reload)) {
        setResubmitError(
          err instanceof ApiError
            ? err.message
            : "Could not send this back to the employee - check your connection and try again."
        );
      } else if (resubmitTarget.kind === "request") {
        setReturnToRequestSheet(null);
      } else {
        setReturnToAdvanceSheet(null);
      }
    } finally {
      setResubmitSaving(false);
    }
  };

  const columns = React.useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor((row) => `${row.requester?.displayName || ""} ${row.requester?.email || ""}`, {
          id: "requester",
          meta: { label: "Requested by" },
          header: "Requested by",
          cell: ({ row }) => {
            const requester = row.original.requester;
            return (
              <div className="min-w-0 max-w-[13.75rem]">
                <p className="truncate font-medium">
                  {requester?.displayName || "-"}
                </p>
                {requester?.email && (
                  <p className="truncate text-xs text-muted-foreground" title={requester.email}>
                    {requester.email}
                  </p>
                )}
              </div>
            );
          }
        }),
        // Travel requests and Other reimbursements share this queue - an
        // Other request has no trip, so its claim type and purpose stand in
        // for the route (same as this page's own Advances Trip column).
        columnHelper.accessor(
          (row) => (row.requestKind === "other" ? row.flightDetails || "" : tripRoute({ ...row, country: row.country || "" })),
          {
            id: "trip",
            meta: { label: "Trip" },
            header: ({ column }) => (
              <SortableHeader column={column}>Trip</SortableHeader>
            ),
            cell: ({ row }) =>
              row.original.requestKind === "other" ? (
                <div className="min-w-0 text-sm">
                  <p className="truncate font-medium">{row.original.flightDetails || "-"}</p>
                  <p className="text-xs text-muted-foreground">{requestTypeLabel(row.original)}</p>
                </div>
              ) : (
                <TripCell trip={{ ...row.original, country: row.original.country || "" }} />
              )
          }
        ),
        columnHelper.accessor((row) => row.flightDetails || "", {
          id: "purpose",
          meta: { label: "Purpose" },
          header: "Purpose",
          cell: ({ row }) => (
            <span className="block max-w-[13.75rem] truncate text-sm" title={row.original.flightDetails || undefined}>
              {row.original.flightDetails || "-"}
            </span>
          )
        }),
        // This queue's own row (FinanceQueueRow, extends Approval) carries
        // only trip fields + status, not the bill/advance data Amount/
        // Settlement amount need - both cells fetch the full request by id
        // instead (see SettlementAmountCell.tsx's own doc comments on why,
        // and how the two share one fetch rather than duplicating it).
        columnHelper.display({
          id: "amount",
          meta: { label: "Amount" },
          header: "Amount",
          cell: ({ row }) => <AmountCellById requestId={row.original.travelRequestId} />
        }),
        columnHelper.display({
          id: "settlementAmount",
          meta: { label: "Settlement amount" },
          header: "Settlement amount",
          cell: ({ row }) => (
            <SettlementAmountCellById requestId={row.original.travelRequestId} />
          )
        }),
        columnHelper.accessor((row) => displayStatus(row), {
          id: "status",
          meta: { label: "Status" },
          header: ({ column }) => (
            <SortableHeader column={column}>Status</SortableHeader>
          ),
          cell: ({ row }) => {
            const shown = displayStatus(row.original);
            return (
              <StatusBadge status={shown} label={STATUS_LABEL[shown]} />
            );
          }
        }),
        columnHelper.accessor("createdAt", {
          id: "received",
          meta: { label: "Received" },
          sortFn: "datetime",
          header: ({ column }) => (
            <SortableHeader column={column}>Received</SortableHeader>
          ),
          cell: ({ row }) => (
            <span className="whitespace-nowrap text-sm text-muted-foreground">
              {formatDate(row.original.createdAt)}
            </span>
          )
        }),
        // columnHelper.display({
        //   id: "decided",
        //   meta: { label: "Decided" },
        //   header: "Decided",
        //   cell: ({ row }) => (
        //     <span className="whitespace-nowrap text-sm text-muted-foreground">
        //       {row.original.decidedAt ? formatDate(row.original.decidedAt) : "-"}
        //     </span>
        //   ),
        // }),
        columnHelper.display({
          id: "view",
          enableHiding: false,
          header: "",
          cell: ({ row }) => (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => setViewRequestId(row.original.travelRequestId)}
              >
                <Eye className="mr-1 h-3.5 w-3.5" /> View
              </Button>
              <DownloadReportButton
                travelRequestId={row.original.travelRequestId}
              />
            </div>
          )
        }),
        columnHelper.display({
          id: "actions",
          enableHiding: false,
          header: () => <div className="text-right">Actions</div>,
          cell: ({ row }) => {
            const r = row.original;
            // Only still actionable while it's genuinely Pending FINANCE's
            // decision. Once Finance has sent it back (requestStatus ===
            // "ResubmitRequested"), financeApproval.status is still "Pending"
            // by design, but the ball is now in the employee's court - no
            // Approve/Reject until they resubmit. displayStatus() collapses
            // both of those into one check.
            if (displayStatus(r) === "Pending") {
              return (
                <div className="flex justify-end">
                  <SplitActionButton
                    label="Approve"
                    icon={<Check className="mr-1 h-3.5 w-3.5" />}
                    onClick={() => {
                      setComment("");
                      setDecision({ row: r, verdict: "Approved" });
                    }}
                  >
                    <DropdownMenuItem
                      onClick={() => setReviewRequestId(r.travelRequestId)}
                    >
                      <Pencil className="mr-2 h-3.5 w-3.5" /> Review
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        setResubmitComment("");
                        setResubmitError(null);
                        setResubmitTarget({
                          kind: "request",
                          id: r.travelRequestId,
                          stage: "Finance"
                        });
                      }}
                    >
                      <RotateCcw className="mr-2 h-3.5 w-3.5" /> Resubmit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                      onClick={() => {
                        setComment("");
                        setDecision({ row: r, verdict: "Rejected" });
                      }}
                    >
                      <X className="mr-2 h-3.5 w-3.5" /> Reject
                    </DropdownMenuItem>
                  </SplitActionButton>
                </div>
              );
            }
            const canSettle =
              r.status === "Approved" && r.requestStatus === "Approved";
            if (!canSettle) {
              return (
                <div className="text-right text-sm text-muted-foreground">
                  -
                </div>
              );
            }
            return (
              <div className="flex justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8"
                  onClick={() => setSettleTarget(r)}
                >
                  <Banknote className="mr-1 h-3.5 w-3.5" /> Settle
                </Button>
              </div>
            );
          }
        })
      ]),
    []
  );

  const advanceColumns = React.useMemo(
    () =>
      advanceColumnHelper.columns([
        advanceColumnHelper.accessor(
          (row) => `${row.requester?.displayName || ""} ${row.requester?.email || ""}`,
          {
            id: "requester",
            meta: { label: "Requested by" },
            header: "Requested by",
            cell: ({ row }) => {
              const requester = row.original.requester;
              return (
                <div className="min-w-0 max-w-[13.75rem]">
                  <p className="truncate font-medium">
                    {requester?.displayName || "-"}
                  </p>
                  {requester?.email && (
                    <p className="truncate text-xs text-muted-foreground" title={requester.email}>
                      {requester.email}
                    </p>
                  )}
                </div>
              );
            }
          }
        ),
        // An Other reimbursement advance has no trip - its claim type and
        // purpose stand in for the route, and it has no travel dates.
        advanceColumnHelper.accessor(
          (row) => (row.requestKind === "other" ? row.flightDetails || "" : tripRoute(row)),
          {
            id: "country",
            meta: { label: "Trip" },
            header: ({ column }) => (
              <SortableHeader column={column}>Trip</SortableHeader>
            ),
            cell: ({ row }) =>
              row.original.requestKind === "other" ? (
                <div className="min-w-0 text-sm">
                  <p className="truncate font-medium">{row.original.flightDetails || "-"}</p>
                  <p className="text-xs text-muted-foreground">{requestTypeLabel(row.original)}</p>
                </div>
              ) : (
                <TripCell trip={row.original} />
              )
          }
        ),
        advanceColumnHelper.accessor((row) => row.flightDetails || "", {
          id: "purpose",
          meta: { label: "Purpose" },
          header: "Purpose",
          cell: ({ row }) => (
            <span className="block max-w-[13.75rem] truncate text-sm" title={row.original.flightDetails || undefined}>
              {row.original.flightDetails || "-"}
            </span>
          )
        }),
        advanceColumnHelper.display({
          id: "dates",
          meta: { label: "Travel dates" },
          header: "Travel dates",
          cell: ({ row }) =>
            row.original.requestKind === "other" ? (
              <span className="whitespace-nowrap text-sm">
                {row.original.advanceDate
                  ? formatDate(row.original.advanceDate)
                  : "-"}
              </span>
            ) : (
              <span className="whitespace-nowrap text-sm">
                {formatDate(row.original.departureAt)} –{" "}
                {formatDate(row.original.arrivalAt)}
              </span>
            )
        }),
        // Sorts by the first currency line's amount (a multi-currency
        // advance has no single comparable total).
        advanceColumnHelper.accessor((row) => row.amounts[0]?.amount ?? 0, {
          id: "amount",
          meta: { label: "Amount" },
          header: ({ column }) => (
            <SortableHeader column={column}>Amount</SortableHeader>
          ),
          cell: ({ row }) => <AdvanceAmountsCell amounts={row.original.amounts} />
        }),
        advanceColumnHelper.accessor("status", {
          id: "status",
          meta: { label: "Status" },
          header: "Status",
          // Indexed by the row's own status rather than assuming one shape
          // per fetch - under "Needs action" a fetch is Pending/Approved
          // only, under "Paid"/"Rejected" it's that one status only, but
          // under "All" (see advances_awaiting_payment) all four can appear
          // together in the same table.
          cell: ({ row }) => (
            <StatusBadge
              status={row.original.status}
              label={ADVANCE_STATUS_LABEL[row.original.status]}
            />
          )
        }),
        advanceColumnHelper.accessor("updatedAt", {
          id: "updated",
          meta: { label: "Last updated" },
          sortFn: "datetime",
          header: ({ column }) => (
            <SortableHeader column={column}>Last updated</SortableHeader>
          ),
          cell: ({ row }) => (
            <span className="whitespace-nowrap text-sm text-muted-foreground">
              {formatDate(row.original.updatedAt)}
            </span>
          )
        }),
        advanceColumnHelper.display({
          id: "view",
          enableHiding: false,
          header: "",
          cell: ({ row }) => (
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => setViewAdvanceId(row.original.id)}
            >
              <Eye className="mr-1 h-3.5 w-3.5" /> View
            </Button>
          )
        }),
        advanceColumnHelper.display({
          id: "actions",
          enableHiding: false,
          header: () => <div className="text-right">Actions</div>,
          cell: ({ row }) => {
            const a = row.original;
            if (a.status === "Pending") {
              // Skipped straight to Finance - no reporting manager on file
              // (see create_advance) - so there's no separate Manager
              // sign-off to show here: the same Mark paid/Reject action
              // every other Finance-stage advance gets, not a duplicate
              // Approve/Reject pair mimicking a Manager's own decision.
              return (
                <div className="flex justify-end">
                  <SplitActionButton
                    label={payingId === a.id ? "Marking paid…" : "Mark paid"}
                    disabled={payingId === a.id}
                    onClick={() => setPayTarget(a)}
                  >
                    <DropdownMenuItem
                      onClick={() =>
                        setReviewAdvance({ id: a.id, stage: "Manager" })
                      }
                    >
                      <Pencil className="mr-2 h-3.5 w-3.5" /> Review
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        setResubmitComment("");
                        setResubmitError(null);
                        setResubmitTarget({
                          kind: "advance",
                          id: a.id,
                          stage: "Manager"
                        });
                      }}
                    >
                      <RotateCcw className="mr-2 h-3.5 w-3.5" /> Resubmit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                      onClick={() => {
                        setAdvanceComment("");
                        setAdvanceDecision({ advance: a });
                      }}
                    >
                      <X className="mr-2 h-3.5 w-3.5" /> Reject
                    </DropdownMenuItem>
                  </SplitActionButton>
                </div>
              );
            }
            if (a.status === "Approved") {
              // Mark paid, with Reject alongside it in the same split-button
              // pattern the Pending row above uses - Finance can turn an
              // approved advance down before paying it out.
              return (
                <div className="flex justify-end">
                  <SplitActionButton
                    label={payingId === a.id ? "Marking paid…" : "Mark paid"}
                    disabled={payingId === a.id}
                    onClick={() => setPayTarget(a)}
                  >
                    <DropdownMenuItem
                      onClick={() =>
                        setReviewAdvance({ id: a.id, stage: "Finance" })
                      }
                    >
                      <Pencil className="mr-2 h-3.5 w-3.5" /> Review
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        setResubmitComment("");
                        setResubmitError(null);
                        setResubmitTarget({
                          kind: "advance",
                          id: a.id,
                          stage: "Finance"
                        });
                      }}
                    >
                      <RotateCcw className="mr-2 h-3.5 w-3.5" /> Resubmit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                      onClick={() => {
                        setAdvanceComment("");
                        setAdvanceDecision({ advance: a });
                      }}
                    >
                      <X className="mr-2 h-3.5 w-3.5" /> Reject
                    </DropdownMenuItem>
                  </SplitActionButton>
                </div>
              );
            }
            // Paid/Rejected - settled, nothing left to action (only under
            // that status filter). Resubmit - sent back for changes; waiting
            // on the employee, same as a travel request's own "Needs
            // changes" row, so there's nothing for this approver to do here
            // either (only under the Resubmit status filter).
            return (
              <div className="text-right text-sm text-muted-foreground">-</div>
            );
          }
        })
      ]),
    [payingId]
  );

  // The row this sheet is currently open for - looked up so the sheet's
  // footerActions can mirror the table row's own actions exactly (same
  // displayStatus/canSettle gates), reused rather than duplicated. undefined
  // (not found, or nothing's open) correctly renders no footer actions.
  const viewRequestRow = rows.find(
    (r) => r.travelRequestId === viewRequestId
  );
  const viewAdvanceRow = advances.find((a) => a.id === viewAdvanceId);

  return (
    <PageContainer fullWidth>
      <PageHeader
        title="Approver Queue"
        description="Travel requests routed to you for final sign-off, after Manager approval."
        titleActions={
          <RefreshButton
            onRefresh={
              view === "requests"
                ? load
                : () => {
                    loadAdvances();
                    loadNeedsActionAdvanceCount();
                  }
            }
            isLoading={view === "requests" ? loading : advancesLoading}
          />
        }
      />

      <Tabs value={view} onValueChange={(v) => setView(v as View)}>
        <TabsList className="grid w-full grid-cols-2 sm:inline-flex sm:w-auto">
          <TabsTrigger value="requests" className="gap-1.5">
            Travel Requests
            {pendingRequestCount > 0 && (
              <TabCountBadge count={pendingRequestCount} />
            )}
          </TabsTrigger>
          <TabsTrigger value="advances" className="gap-1.5">
            Advances
            {needsActionAdvanceCount > 0 && (
              <TabCountBadge count={needsActionAdvanceCount} />
            )}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {view === "requests" ? (
        <DataTable
          columns={columns}
          data={data}
          isLoading={loading}
          filterColumn="requester"
          filterPlaceholder="Search name or email…"
          emptyMessage={EMPTY_COPY[status]}
          persistKey="approver-queue:requests"
          toolbarStart={
            <SegmentedTabs
              value={status}
              onChange={setStatus}
              options={[
                { value: "All", label: "All" },
                { value: "Pending", label: "Pending" },
                { value: "ResubmitRequested", label: "Needs changes" },
                { value: "Approved", label: "Approved" },
                { value: "Rejected", label: "Rejected" },
                { value: "Completed", label: "Completed" }
              ]}
            />
          }
        />
      ) : (
        <DataTable
          columns={advanceColumns}
          data={advances}
          isLoading={advancesLoading}
          filterColumn="requester"
          filterPlaceholder="Search name or email…"
          emptyMessage={ADVANCE_EMPTY_COPY[advanceStatus]}
          persistKey="approver-queue:advances"
          toolbarStart={
            <SegmentedTabs
              value={advanceStatus}
              onChange={setAdvanceStatus}
              options={[
                { value: "All", label: "All" },
                { value: "NeedsAction", label: "Needs action" },
                { value: "Resubmit", label: "Needs changes" },
                { value: "Paid", label: "Paid" },
                { value: "Rejected", label: "Rejected" }
              ]}
            />
          }
        />
      )}

      <ConfirmDialog
        open={!!decision}
        onOpenChange={(open) =>
          !open && cancelAndMaybeReturnToRequestSheet(() => setDecision(null))
        }
        title={
          decision?.verdict === "Approved"
            ? "Approve this request?"
            : "Reject this request?"
        }
        description={
          decision?.verdict === "Rejected"
            ? "Tell the requester why this is being rejected - required, recorded in the audit log."
            : "Add an optional note for the requester. This decision is recorded in the audit log."
        }
        confirmLabel={decision?.verdict === "Approved" ? "Approve" : "Reject"}
        destructive={decision?.verdict === "Rejected"}
        pending={saving}
        confirmDisabled={decision?.verdict === "Rejected" && !comment.trim()}
        onConfirm={submitDecision}
      >
        <Input
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={
            decision?.verdict === "Rejected"
              ? "Reason for rejection (required)"
              : "Comment (optional)"
          }
        />
      </ConfirmDialog>

      <ConfirmDialog
        open={!!advanceDecision}
        onOpenChange={(open) =>
          !open &&
          cancelAndMaybeReturnToAdvanceSheet(() => setAdvanceDecision(null))
        }
        title="Reject this advance?"
        description={
          advanceDecision?.advance.status === "Approved"
            ? "The Manager has already approved this advance. Rejecting it now means it won't be paid out, and the requester is notified. Tell them why - required, recorded in the audit log."
            : "Tell the requester why this is being rejected - required, recorded in the audit log."
        }
        confirmLabel="Reject"
        destructive
        pending={advanceSaving}
        confirmDisabled={!advanceComment.trim()}
        onConfirm={submitAdvanceDecision}
      >
        <Input
          value={advanceComment}
          onChange={(e) => setAdvanceComment(e.target.value)}
          placeholder="Reason for rejection (required)"
        />
      </ConfirmDialog>

      <ConfirmDialog
        open={!!payTarget}
        onOpenChange={(open) =>
          !open && cancelAndMaybeReturnToAdvanceSheet(() => setPayTarget(null))
        }
        title="Mark this advance as paid?"
        description="Records the disbursement and notifies the requester. This can't be undone from here."
        confirmLabel="Mark paid"
        pending={!!payTarget && payingId === payTarget.id}
        onConfirm={confirmMarkPaid}
      />

      <SettleRequestDialog
        travelRequestId={settleTarget?.travelRequestId ?? null}
        open={!!settleTarget}
        onOpenChange={(open) =>
          !open &&
          cancelAndMaybeReturnToRequestSheet(() => setSettleTarget(null))
        }
        pending={settling}
        onConfirm={submitSettle}
      />

      <RequestDetailDialog
        travelRequestId={viewRequestId}
        open={!!viewRequestId}
        onOpenChange={(open) => {
          if (!open) {
            closeViewRequestSheet();
          }
        }}
        highlight={highlightOnOpen}
        footerActions={
          viewRequestRow &&
          (displayStatus(viewRequestRow) === "Pending" ? (
            // Identical to the table row's own "actions" column cell (the
            // Pending branch) - same SplitActionButton, same three dropdown
            // items, same state setters - EXCEPT each one also steps aside
            // from this sheet (stepAsideFromRequestSheet) first, so its own
            // dialog doesn't double up two semi-transparent overlays into
            // solid black, and canceling that dialog brings this sheet back
            // instead of dropping onto the bare table.
            <SplitActionButton
              label="Approve"
              icon={<Check className="mr-1 h-3.5 w-3.5" />}
              onClick={() => {
                stepAsideFromRequestSheet();
                setComment("");
                setDecision({ row: viewRequestRow, verdict: "Approved" });
              }}
            >
              <DropdownMenuItem
                onClick={() => {
                  stepAsideFromRequestSheet();
                  setReviewRequestId(viewRequestRow.travelRequestId);
                }}
              >
                <Pencil className="mr-2 h-3.5 w-3.5" /> Review
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  stepAsideFromRequestSheet();
                  setResubmitComment("");
                  setResubmitError(null);
                  setResubmitTarget({
                    kind: "request",
                    id: viewRequestRow.travelRequestId,
                    stage: "Finance"
                  });
                }}
              >
                <RotateCcw className="mr-2 h-3.5 w-3.5" /> Resubmit
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                onClick={() => {
                  stepAsideFromRequestSheet();
                  setComment("");
                  setDecision({ row: viewRequestRow, verdict: "Rejected" });
                }}
              >
                <X className="mr-2 h-3.5 w-3.5" /> Reject
              </DropdownMenuItem>
            </SplitActionButton>
          ) : (
            viewRequestRow.status === "Approved" &&
            viewRequestRow.requestStatus === "Approved" && (
              // Identical to the table row's own "Settle" button (the
              // canSettle branch) - same step-aside-first reasoning as the
              // SplitActionButton above.
              <Button
                size="sm"
                variant="outline"
                className="h-8"
                onClick={() => {
                  stepAsideFromRequestSheet();
                  setSettleTarget(viewRequestRow);
                }}
              >
                <Banknote className="mr-1 h-3.5 w-3.5" /> Settle
              </Button>
            )
          ))
        }
      />
      <AdvanceDetailDialog
        advanceId={viewAdvanceId}
        open={!!viewAdvanceId}
        onOpenChange={(open) => {
          if (!open) {
            closeViewAdvanceSheet();
          }
        }}
        highlight={highlightOnOpen}
        footerActions={
          viewAdvanceRow &&
          (viewAdvanceRow.status === "Pending" ||
            viewAdvanceRow.status === "Approved") && (
            // Identical to the Advances table's own "actions" column cell
            // (its Pending/Approved branches are otherwise near-identical,
            // differing only in the Review stage and Resubmit stage below) -
            // same step-aside-first reasoning as the request sheet above.
            <SplitActionButton
              label={payingId === viewAdvanceRow.id ? "Marking paid…" : "Mark paid"}
              disabled={payingId === viewAdvanceRow.id}
              onClick={() => {
                stepAsideFromAdvanceSheet();
                setPayTarget(viewAdvanceRow);
              }}
            >
              <DropdownMenuItem
                onClick={() => {
                  stepAsideFromAdvanceSheet();
                  setReviewAdvance({
                    id: viewAdvanceRow.id,
                    stage:
                      viewAdvanceRow.status === "Pending"
                        ? "Manager"
                        : "Finance"
                  });
                }}
              >
                <Pencil className="mr-2 h-3.5 w-3.5" /> Review
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  stepAsideFromAdvanceSheet();
                  setResubmitComment("");
                  setResubmitError(null);
                  setResubmitTarget({
                    kind: "advance",
                    id: viewAdvanceRow.id,
                    stage:
                      viewAdvanceRow.status === "Pending"
                        ? "Manager"
                        : "Finance"
                  });
                }}
              >
                <RotateCcw className="mr-2 h-3.5 w-3.5" /> Resubmit
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                onClick={() => {
                  stepAsideFromAdvanceSheet();
                  setAdvanceComment("");
                  setAdvanceDecision({ advance: viewAdvanceRow });
                }}
              >
                <X className="mr-2 h-3.5 w-3.5" /> Reject
              </DropdownMenuItem>
            </SplitActionButton>
          )
        }
      />

      <ReviewRequestDialog
        travelRequestId={reviewRequestId}
        open={!!reviewRequestId}
        onOpenChange={(open) =>
          !open &&
          cancelAndMaybeReturnToRequestSheet(() => setReviewRequestId(null))
        }
        onSave={saveRequestReview}
        saving={reviewSaving}
      />
      <ReviewAdvanceDialog
        advanceId={reviewAdvance?.id || null}
        open={!!reviewAdvance}
        onOpenChange={(open) =>
          !open &&
          cancelAndMaybeReturnToAdvanceSheet(() => setReviewAdvance(null))
        }
        onSave={saveAdvanceReview}
        saving={reviewSaving}
      />

      {financeNotice && (
        <FinanceNoticeDialog
          open
          onClose={() => {
            // Only reached by closing WITHOUT sending (a successful Send
            // closes via sendFinanceNotice's own direct setFinanceNotice
            // (null) call, a separate code path) - per this dialog's own
            // design, that leaves the advance exactly as it was, so this is
            // the same "nothing was actually decided" cancel case the other
            // dialogs already handle. financeNotice here is advance-only
            // (see its own doc comment), so only returnToAdvanceSheet
            // applies.
            cancelAndMaybeReturnToAdvanceSheet(() => {
              setFinanceNotice(null);
              setFinanceCc([]);
              setFinanceError(null);
            });
          }}
          toName={financeNotice.toName}
          toEmail={financeNotice.toEmail}
          cc={financeCc}
          onCcChange={setFinanceCc}
          onSend={sendFinanceNotice}
          sending={financeSending}
          error={financeError}
        />
      )}

      <ConfirmDialog
        open={!!resubmitTarget}
        onOpenChange={(open) => {
          if (open) return;
          // resubmitTarget serves BOTH sheets (kind: "request" | "advance")
          // from one dialog - the kind must be read BEFORE closing clears
          // resubmitTarget, or there'd be no way to tell which (if either)
          // returnTo*Sheet applies.
          const kind = resubmitTarget?.kind;
          if (kind === "advance") {
            cancelAndMaybeReturnToAdvanceSheet(() => setResubmitTarget(null));
          } else {
            cancelAndMaybeReturnToRequestSheet(() => setResubmitTarget(null));
          }
        }}
        title={
          resubmitTarget?.kind === "advance"
            ? "Send this advance back for changes?"
            : "Send this request back for changes?"
        }
        description="The employee will be able to edit it and resubmit - it comes straight back to you, no need to decide it again from scratch."
        confirmLabel="Send back"
        pending={resubmitSaving}
        confirmDisabled={!resubmitComment.trim()}
        onConfirm={submitResubmit}
      >
        <Input
          value={resubmitComment}
          onChange={(e) => setResubmitComment(e.target.value)}
          placeholder="What needs to change? (required)"
        />
        {resubmitError && (
          <p className="text-sm text-destructive">{resubmitError}</p>
        )}
      </ConfirmDialog>
    </PageContainer>
  );
}
